/**
 * Mechanic duties (+encounters, docs/design/encounters.md §5). Each bot derives the same
 * assignments from the shared world (like the kick rotation), so no messaging is needed.
 *
 * Urgent duties (handled right after dodging, before kicks):
 *   tether     - run straight out until the tether breaks (breakAt + margin);
 *   soak       - assigned soakers walk into their soak and hold;
 *   behind     - `counter: 'behindCaster'` casts: everyone, tank included, into the safe wedge;
 *   lookAway   - `counter: 'lookAway'` casts: turn your back just before the cast ends;
 *   kite       - a tracking beam (zone motion 'aim') or `counter: 'kite'` cast on me: circle the caster close in;
 *   explode    - a hazard debuff about to explode: get `radius` away from allies.
 * Positional duties (replace normal positioning):
 *   isolate    - a jump-on-dispel / isolate debuff: step away from allies so the healer can dispel;
 *   fixate     - fixated by an add: kite it back to the tank;
 *   spread     - a `counter: 'spread'` cast (random circles / lines): pre-spread so nothing overlaps (spread.ts);
 *   clear      - a tracking beam on someone else: stand on the far side of its anchor (spread.ts).
 *
 * Every duty is perceived after `reactionMs` and passes one `dodgeReliability` roll per instance.
 */
import type { AuraDef, DeepReadonly, SoakState, UnitId, Vec2, WorldShape, Zone } from '@keystone/sim';
import { add, angleDiff, angleOf, dist, fromAngle, norm, scale, sub } from '../geometry';
import type { ClassKit } from '../kits';
import type { BotMemory } from '../memory';
import type { DangerArea, Perception, RUnit } from '../perception';
import { inDanger } from './dodge';
import { castKickPriority } from './kick';
import { awayFromAllies, castCounters, hold, moveTo, ringPoint, seen, walkable, type Duty } from './duty';
import { beamClearDuty, spreadDuty } from './spread';

export type { Duty } from './duty';

/** Extra distance past a tether's break range. */
const TETHER_MARGIN = 3;
/** Radius a beam target circles the caster at (small radius = faster angular speed than the beam). */
const KITE_RADIUS = 8;
/** Explosion prep window. */
const EXPLODE_PREP_MS = 4000;
/** Look-away lead before the gaze resolves. */
const LOOK_AWAY_LEAD_MS = 900;

type RSoak = DeepReadonly<SoakState>;

// ---------------------------------------------------------------------------
// Soak assignment
// ---------------------------------------------------------------------------

/** Nearest point inside a placed shape (soak target). */
export function soakSpot(area: DeepReadonly<WorldShape>, from: Vec2): Vec2 {
  const s = area.shape;
  if (s.type === 'line') {
    const dir = fromAngle(area.facing);
    const rel = sub(from, area.origin);
    const along = Math.max(Math.min(2, s.length / 2), Math.min(s.length - Math.min(3, s.length / 2), rel.x * dir.x + rel.y * dir.y));
    return add(area.origin, scale(dir, along));
  }
  return { x: area.origin.x, y: area.origin.y };
}

function auraDef(p: Perception, id: string): AuraDef | undefined {
  return p.view.content.aura(id);
}

/** Stacks of `auraId` on `u` (0 if none). */
function stacksOf(u: RUnit, auraId: string): number {
  let n = 0;
  for (const a of u.auras) if (a.auraId === auraId) n += a.stacks;
  return n;
}

/** Is this unit busy with a duty that pins it elsewhere (tether, fixate)? */
function pinned(p: Perception, u: RUnit): boolean {
  if (p.world.mechanics?.tethers.some((t) => t.unitId === u.id)) return true;
  return p.engaged.some((e) => e.threat?.fixate?.on === u.id && e.tier !== 'boss');
}

/**
 * Soak assignments are decided once per soak (the first think that sees it) and kept, so two
 * bots never swap mid-run as positions change. Keyed by the shared soak id; every bot derives
 * it from the same world, so they agree without messaging.
 */
const soakMemo = new WeakMap<object, Map<string, UnitId[]>>();

/** Deterministic soak assignment: soak id → soaker ids (same result on every bot). */
export function assignSoaks(p: Perception, memoKey?: object): Map<string, UnitId[]> {
  const soaks = p.world.mechanics?.soaks ?? [];
  const memo = memoKey ? soakMemo.get(memoKey) : undefined;
  const alive = (id: UnitId): boolean => !!p.world.units[id]?.alive;
  if (memo && soaks.every((s) => memo.get(s.id)?.every(alive) && (memo.get(s.id)?.length ?? 0) > 0)) return memo;
  const fresh = computeSoaks(p, memo);
  if (memoKey) {
    // Keep only live soaks.
    const live = new Map<string, UnitId[]>();
    for (const s of soaks) live.set(s.id, fresh.get(s.id) ?? []);
    soakMemo.set(memoKey, live);
    return live;
  }
  return fresh;
}

function computeSoaks(p: Perception, keep?: Map<string, UnitId[]>): Map<string, UnitId[]> {
  const out = new Map<string, UnitId[]>();
  const soaks = p.world.mechanics?.soaks ?? [];
  if (soaks.length === 0) return out;
  const used = new Set<UnitId>();
  // Earlier decisions stand.
  for (const s of soaks) {
    const prev = keep?.get(s.id)?.filter((id) => p.world.units[id]?.alive);
    if (!prev || prev.length === 0) continue;
    out.set(s.id, prev);
    for (const id of prev) used.add(id);
  }
  const slot = (u: RUnit): number => p.world.partyIds.indexOf(u.id);
  for (const s of soaks as readonly RSoak[]) {
    if (out.has(s.id)) continue;
    let pool = p.party.filter((u) => !used.has(u.id) && !pinned(p, u));
    if (s.eligibleAura) {
      // 'enter' soaks that consume stacks (Bone Shards): the holder with the most stacks (≥ 2).
      pool = pool.filter((u) => stacksOf(u, s.eligibleAura!) >= 2).sort((a, b) => stacksOf(b, s.eligibleAura!) - stacksOf(a, s.eligibleAura!) || slot(a) - slot(b));
    } else {
      const cost = (u: RUnit): number => dist(soakSpot(s.area, u.pos), u.pos) + (u.role === 'healer' ? 12 : 0);
      pool = pool.sort((a, b) => cost(a) - cost(b) || slot(a) - slot(b));
    }
    const need = s.eligibleAura ? 1 : Math.max(1, s.min);
    const picked = pool.slice(0, need).map((u) => u.id);
    for (const id of picked) used.add(id);
    out.set(s.id, picked);
  }
  return out;
}

/** Soak telegraph ids this bot should NOT treat as danger (assigned, or not eligible to be hit). */
export function safeSoakIds(p: Perception, mem?: BotMemory): Set<string> {
  const out = new Set<string>();
  const soaks = (p.world.mechanics?.soaks ?? []) as readonly RSoak[];
  if (soaks.length === 0) return out;
  const asg = assignSoaks(p, mem);
  for (const s of soaks) {
    if (asg.get(s.id)?.includes(p.self.id)) out.add(s.id);
    else if (s.eligibleAura && stacksOf(p.self, s.eligibleAura) === 0) out.add(s.id);
  }
  return out;
}

// ---------------------------------------------------------------------------
// Duties
// ---------------------------------------------------------------------------

function tetherDuty(p: Perception, mem: BotMemory, dangers: readonly DangerArea[]): Duty | null {
  const t = p.world.mechanics?.tethers.find((x) => x.unitId === p.self.id);
  if (!t) return null;
  const src = p.world.units[t.sourceId];
  if (!src || !seen(p, mem, `tether:${t.id}`, t.startedAt)) return null;
  const away = angleOf(sub(p.self.pos, src.pos));
  const dest = ringPoint(p, src.pos, away, t.breakAt + TETHER_MARGIN, dangers) ?? add(src.pos, scale(fromAngle(away), t.breakAt + TETHER_MARGIN));
  return { kind: 'tether', urgent: true, cmds: moveTo(p, mem, dest) };
}

function soakDuty(p: Perception, mem: BotMemory): Duty | null {
  const soaks = (p.world.mechanics?.soaks ?? []) as readonly RSoak[];
  if (soaks.length === 0) return null;
  const asg = assignSoaks(p, mem);
  for (const s of soaks) {
    if (!asg.get(s.id)?.includes(p.self.id)) continue;
    const tg = p.world.telegraphs.find((t) => t.id === s.id);
    if (!seen(p, mem, `soak:${s.id}`, tg?.createdAt ?? p.now)) continue;
    const spot = soakSpot(s.area, p.self.pos);
    const inside = p.view.queries.spatial.shapeContains(s.area as WorldShape, p.self.pos, 0);
    if (inside && dist(p.self.pos, spot) < 1.5) return { kind: 'soak', urgent: true, cmds: hold(p) };
    return { kind: 'soak', urgent: true, cmds: moveTo(p, mem, spot) };
  }
  return null;
}

function behindDuty(p: Perception, mem: BotMemory): Duty | null {
  const c = castCounters(p, mem, 'behindCaster').find((e) => dist(e.pos, p.self.pos) < 45);
  if (!c?.cast || !seen(p, mem, `behind:${c.id}:${c.cast.startedAt}`, c.cast.startedAt)) return null;
  const back = c.facing + Math.PI;
  const rel = sub(p.self.pos, c.pos);
  const safe = Math.abs(angleDiff(angleOf(rel), back)) < (25 * Math.PI) / 180 && dist(p.self.pos, c.pos) < 12;
  if (safe) return { kind: 'behind', urgent: true, cmds: hold(p) };
  // Spread a little inside the wedge so four bots don't stack on one point.
  const slot = Math.max(0, p.world.partyIds.indexOf(p.self.id));
  const off = ((slot - 1.5) * 8 * Math.PI) / 180;
  let dest = add(c.pos, scale(fromAngle(back + off), c.radius + 3));
  // Ranged far in front: leaving the cone's reach is shorter than running around the boss.
  const shape = p.view.content.enemyAbility(c.cast.abilityId)?.shape;
  const reach = shape?.type === 'cone' ? shape.range : shape?.type === 'circle' ? shape.radius : undefined;
  if (reach !== undefined) {
    const out = add(c.pos, scale(norm(rel), reach + 3));
    if (walkable(p, out) && dist(out, p.self.pos) < dist(dest, p.self.pos)) dest = out;
  }
  return { kind: 'behind', urgent: true, cmds: moveTo(p, mem, dest) };
}

function lookAwayDuty(p: Perception, mem: BotMemory): Duty | null {
  const c = castCounters(p, mem, 'lookAway').find((e) => dist(e.pos, p.self.pos) < 45);
  if (!c?.cast || c.cast.endsAt - p.now > LOOK_AWAY_LEAD_MS + mem.skill.reactionMs) return null;
  if (!seen(p, mem, `look:${c.id}:${c.cast.startedAt}`, c.cast.startedAt)) return null;
  const away = angleOf(sub(p.self.pos, c.pos));
  if (Math.abs(angleDiff(p.self.facing, away)) < 0.4) return { kind: 'lookAway', urgent: true, cmds: [] };
  return { kind: 'lookAway', urgent: true, cmds: [{ type: 'face', actorId: p.self.id, facing: away }] };
}

/** Tracking beam aimed at me (zone motion 'aim'), or a `kite` cast with me as target. */
function kiteDuty(p: Perception, mem: BotMemory, dangers: readonly DangerArea[]): Duty | null {
  let anchor: RUnit | undefined;
  let beamFacing: number | undefined;
  let key = '';
  let since = 0;
  for (const z of p.world.zones as readonly DeepReadonly<Zone>[]) {
    const m = z.motion;
    if (m?.spec.type !== 'aim' || m.targetId !== p.self.id) continue;
    anchor = p.world.units[m.anchorId ?? z.sourceId];
    beamFacing = z.area.facing;
    key = `kite:${z.id}`;
    since = z.createdAt;
  }
  if (!anchor) {
    const c = castCounters(p, mem, 'kite').find((e) => e.cast?.targetId === p.self.id);
    if (c?.cast) {
      anchor = c;
      beamFacing = angleOf(sub(p.self.pos, c.pos));
      key = `kite:${c.id}:${c.cast.startedAt}`;
      since = c.cast.startedAt;
    }
  }
  if (!anchor || beamFacing === undefined || !seen(p, mem, key, since)) return null;
  const mine = angleOf(sub(p.self.pos, anchor.pos));
  // Run the way that takes me away from where the beam points now.
  const sign = angleDiff(mine, beamFacing) >= 0 ? 1 : -1;
  const r = Math.max(KITE_RADIUS, anchor.radius + 4);
  const target = add(anchor.pos, scale(fromAngle(mine + sign * (Math.PI / 3)), r));
  const dest = walkable(p, target) && !inDanger(p, dangers.filter((d) => d.kind !== 'zone'), target, p.self.radius) ? target : ringPoint(p, anchor.pos, mine + sign * (Math.PI / 3), r, []) ?? target;
  return { kind: 'kite', urgent: true, cmds: moveTo(p, mem, dest) };
}

function explodeDuty(p: Perception, mem: BotMemory, dangers: readonly DangerArea[]): Duty | null {
  for (const a of p.self.auras) {
    const def = auraDef(p, a.auraId);
    if (!def?.explodeOnExpire || a.expiresAt === null) continue;
    const left = a.expiresAt - p.now;
    if (left > EXPLODE_PREP_MS + mem.skill.reactionMs || left < 0) continue;
    if (!seen(p, mem, `explode:${a.iid}`, a.expiresAt - EXPLODE_PREP_MS - mem.skill.reactionMs)) continue;
    const dest = awayFromAllies(p, def.explodeOnExpire.radius + 1.5, dangers);
    if (dest) return { kind: 'explode', urgent: true, cmds: moveTo(p, mem, dest) };
  }
  return null;
}

function isolateDuty(p: Perception, mem: BotMemory, dangers: readonly DangerArea[]): Duty | null {
  for (const a of p.self.auras) {
    const def = auraDef(p, a.auraId);
    const radius = def?.jumpOnDispel?.radius ?? (def?.hazard?.intent === 'isolate' ? def.hazard.radius : undefined);
    if (radius === undefined) continue;
    if (!seen(p, mem, `isolate:${a.iid}`, a.appliedAt)) continue;
    const healer = p.party.find((u) => u.role === 'healer' && u.id !== p.self.id);
    const dest = awayFromAllies(p, radius + 1, dangers, healer?.pos);
    if (dest) return { kind: 'isolate', urgent: false, cmds: moveTo(p, mem, dest) };
  }
  return null;
}

function fixateDuty(p: Perception, kit: ClassKit, mem: BotMemory): Duty | null {
  if (kit.role === 'tank') return null;
  const add_ = p.engaged.find((e) => e.threat?.fixate?.on === p.self.id && e.tier !== 'boss');
  const tank = p.tank;
  if (!add_ || !tank?.alive || tank.id === p.self.id) return null;
  if (!seen(p, mem, `fixate:${add_.id}:${add_.threat?.fixate?.until ?? 0}`, p.now - mem.skill.reactionMs)) return null;
  const dest = add(tank.pos, scale(norm(sub(tank.pos, add_.pos)), 3));
  return { kind: 'fixate', urgent: false, cmds: moveTo(p, mem, dest) };
}

/** The most important mechanic duty for this bot right now (null = play normally). */
export function planDuty(p: Perception, kit: ClassKit, mem: BotMemory, dangers: readonly DangerArea[]): Duty | null {
  if (p.engaged.length === 0 && !p.world.mechanics) return null;
  return (
    tetherDuty(p, mem, dangers) ??
    soakDuty(p, mem) ??
    behindDuty(p, mem) ??
    kiteDuty(p, mem, dangers) ??
    explodeDuty(p, mem, dangers) ??
    lookAwayDuty(p, mem) ??
    isolateDuty(p, mem, dangers) ??
    fixateDuty(p, kit, mem) ??
    spreadDuty(p, kit, mem, dangers) ??
    beamClearDuty(p, kit, mem, dangers)
  );
}

// ---------------------------------------------------------------------------
// Target hints
// ---------------------------------------------------------------------------

function immune(p: Perception, u: RUnit): boolean {
  return u.auras.some((a) => auraDef(p, a.auraId)?.flags?.immuneDamage);
}

/** Gated casts up to this kick priority make DPS burst the caster's shield. */
const BURST_PRIORITY = 3;

/**
 * Burst target: an enemy casting a high-priority shield-gated spell while its gating shield is up
 * (Feeding Frenzy behind Gorged Hide, a warded caster). Break the shield, then the kick lands.
 */
function burstTarget(p: Perception, u: RUnit): boolean {
  if (!u.cast || u.cast.interruptible || u.cast.endsAt <= p.now) return false;
  const ab = p.view.content.enemyAbility(u.cast.abilityId);
  return !!ab?.interruptible && !!ab.interruptibleWhen && castKickPriority(p, u) <= BURST_PRIORITY;
}

/** Kill-priority adds: boss summons with a `killAdd` ability, anything enraged, and adds fixating a non-tank. */
function priorityAdd(p: Perception, u: RUnit): boolean {
  if (u.tier === 'boss') return false;
  if (u.auras.some((a) => auraDef(p, a.auraId)?.dispelType === 'enrage')) return true;
  const fix = u.threat?.fixate;
  if (fix && fix.until > p.now && p.world.units[fix.on]?.role !== 'tank') return true;
  const summoner = u.summonerId ? p.world.units[u.summonerId] : undefined;
  if (summoner?.tier !== 'boss') return false;
  const def = u.npcId ? p.view.content.npc(u.npcId) : undefined;
  return !!def?.abilities.some((id) => p.view.content.enemyAbility(id)?.counter?.includes('killAdd'));
}

/** Kill-priority targets further than this are left to whoever is closer. */
const PRIORITY_RANGE = 45;

/**
 * Mechanic-driven target override (DPS/healer): first burst a shield-gated high-priority caster,
 * then burn kill-priority adds (cultists, writhes, enraged or fixating mobs), lowest HP% first.
 * Nobody keeps hitting an immune boss. Returns undefined to keep `current`.
 */
export function mechanicTarget(p: Perception, kit: ClassKit, current: RUnit | undefined): RUnit | undefined {
  if (p.engaged.length === 0) return undefined;
  if (kit.role !== 'tank') {
    const near = (e: RUnit): boolean => dist(e.pos, p.self.pos) <= PRIORITY_RANGE;
    const burst = p.engaged.find((e) => near(e) && !immune(p, e) && burstTarget(p, e));
    if (burst) return burst;
    let best: RUnit | undefined;
    for (const e of p.engaged) {
      if (!near(e) || immune(p, e) || !priorityAdd(p, e)) continue;
      if (!best || e.hp / e.maxHp < best.hp / best.maxHp - 1e-6) best = e;
    }
    if (best) return best;
  }
  if (current && immune(p, current)) {
    let best: RUnit | undefined;
    for (const e of p.engaged) if (!immune(p, e) && (!best || dist(e.pos, p.self.pos) < dist(best.pos, p.self.pos))) best = e;
    return best;
  }
  return undefined;
}

/** Dispel gate for jump-on-dispel debuffs: dispel when nobody is in jump range, or the holder is low. */
export function safeToDispel(p: Perception, holder: RUnit, auraId: string): boolean {
  const def = auraDef(p, auraId);
  const r = def?.jumpOnDispel?.radius;
  if (r === undefined) return true;
  if (holder.maxHp > 0 && holder.hp / holder.maxHp < 0.4) return true;
  return p.party.every((u) => u.id === holder.id || dist(u.pos, holder.pos) > r + 0.5);
}

/** Healing absorb left on a unit (counts as missing HP for triage). */
export function healAbsorbOn(p: Perception, u: RUnit): number {
  let n = 0;
  for (const a of u.auras) if (a.absorbRemaining && auraDef(p, a.auraId)?.healAbsorb) n += a.absorbRemaining;
  return n;
}
